import { WhatsAppLogin } from '@components/forms/login/SocialLoginForm/WhatsAppLogin/WhatsAppLogin';
import { AppSettingContextData } from '@contexts/appSetting/AppSettingContext';
import { AppSetting, FeatureName } from '@ul/appsetting-model';
import { WhiteLabelFeature, WhiteLabelSetting } from '@ul/whitelabel-setting';

export const mergeAppSettingWithWhiteLabelSetting = (
    appSetting: AppSetting,
    whiteLabelSetting: WhiteLabelSetting | undefined
): AppSettingContextData => {
    const { features, customUI, grantType } = appSetting;

    return {
        customUI, // TODO Add functionality to modify custom UI based on whitelabel
        feature: mergeFeature(features, whiteLabelSetting),
        tokenGrantType: grantType,
    };
};

const mergeFeature = (
    features: AppSetting['features'],
    whiteLabelSetting: WhiteLabelSetting | undefined
): AppSettingContextData['feature'] => {
    if (!features) {
        return {};
    }

    const haveWLFeature = !!whiteLabelSetting && !!whiteLabelSetting.features;
    const haveWLEmailLogin = whiteLabelSetting?.features?.includes(WhiteLabelFeature.Login);
    const haveWLPhoneLogin = whiteLabelSetting?.features?.includes(WhiteLabelFeature.PhoneLogin);

    return features.reduce<AppSettingContextData['feature']>((feature, f) => {
        const loginWlSetting = whiteLabelSetting?.config?.login;

        switch (f) {
            case FeatureName.EmailLogin:
                // whitelabel allow to have this feature
                // but no config, then use feature from consul
                if (haveWLFeature && !haveWLEmailLogin) {
                    return feature;
                }

                feature.emailLogin = {};

                if (!loginWlSetting) {
                    return feature;
                }

                if (!!loginWlSetting.usernameRegex) {
                    feature.emailLogin.usernameRegex = loginWlSetting.usernameRegex;
                }

                if (!!loginWlSetting.passwordRegex) {
                    feature.emailLogin.passwordRegex = loginWlSetting.passwordRegex;
                }

                if (!!loginWlSetting.thirdPartyAuth) {
                    feature.emailLogin.thirdPartyAuth = loginWlSetting.thirdPartyAuth;
                }

                return feature;
            case FeatureName.PhoneLogin:
                // whitelabel allow to have this feature
                // but no config, then use feature from consul
                if (haveWLFeature && !haveWLPhoneLogin) {
                    return feature;
                }

                feature.phoneLogin = {};

                if (!loginWlSetting) {
                    return feature;
                }

                if (!!loginWlSetting.passwordRegex) {
                    feature.phoneLogin.passwordRegex = loginWlSetting.passwordRegex;
                }

                return feature;
            case FeatureName.EmailResetPassword:
                // whitelabel allow to have this feature
                // but no config, then use feature from consul
                if (haveWLFeature && !haveWLEmailLogin) {
                    return feature;
                }

                feature.emailResetPassword = {};

                if (!loginWlSetting) {
                    return feature;
                }

                if (!!loginWlSetting.resetPasswordRedirect && !!loginWlSetting.resetPasswordUrl) {
                    feature.emailResetPassword.resetPasswordRedirect = loginWlSetting.resetPasswordRedirect;
                    feature.emailResetPassword.resetPasswordUrl = loginWlSetting.resetPasswordUrl;
                }

                return feature;
            case FeatureName.PhoneResetPassword:
                // whitelabel allow to have this feature
                // but no config, then use feature from consul
                if (haveWLFeature && !haveWLPhoneLogin) {
                    return feature;
                }

                feature.phoneResetPassword = {};

                if (!loginWlSetting) {
                    return feature;
                }

                if (!!loginWlSetting.resetPasswordRedirect && !!loginWlSetting.resetPasswordUrl) {
                    feature.phoneResetPassword.resetPasswordRedirect = loginWlSetting.resetPasswordRedirect;
                    feature.phoneResetPassword.resetPasswordUrl = loginWlSetting.resetPasswordUrl;
                }

                return feature;
            case FeatureName.FacebookLogin:
                // Filter if feature not available in whitelabel features
                if (haveWLFeature && !whiteLabelSetting?.features?.includes(WhiteLabelFeature.FacebookLogin)) {
                    return feature;
                }

                // Filter if config missing
                const fbWlSetting = whiteLabelSetting?.config?.facebook;
                if (!fbWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    facebookLogin: {
                        appId: fbWlSetting.appId,
                    },
                };
            case FeatureName.WeChatLogin:
                // Filter if config missing
                const wechatWlSetting = whiteLabelSetting?.config?.weChat;
                if (!wechatWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    weChatLogin: {
                        appId: wechatWlSetting.appId,
                        domain: wechatWlSetting.domain,
                    },
                };
            case FeatureName.KakaoLogin:
                // Filter if config missing
                const kakaoWlSetting = whiteLabelSetting?.config?.kakao;
                if (!kakaoWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    kakaoLogin: {
                        appKey: kakaoWlSetting.appKey,
                    },
                };
            case FeatureName.LineLogin:
                // Filter if config missing
                const lineWlSetting = whiteLabelSetting?.config?.line;
                if (!lineWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    lineLogin: {
                        clientId: lineWlSetting.clientId,
                    },
                };
            case FeatureName.GoogleLogin:
                // Filter if feature not available in whitelabel features
                if (haveWLFeature && !whiteLabelSetting?.features?.includes(WhiteLabelFeature.GoogleLogin)) {
                    return feature;
                }

                // Filter if config missing
                const googleWlSetting = whiteLabelSetting?.config?.google;
                if (!googleWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    googleLogin: {
                        clientId: googleWlSetting.clientId,
                    },
                };
            case FeatureName.AppleLogin:
                // Filter if feature not available in whitelabel features
                if (haveWLFeature && !whiteLabelSetting?.features?.includes(WhiteLabelFeature.AppleLogin)) {
                    return feature;
                }

                // Filter if config missing
                const appleWlSetting = whiteLabelSetting?.config?.apple;
                if (!appleWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    appleLogin: {
                        clientId: appleWlSetting.clientId,
                    },
                };
            case FeatureName.Signup:
                // Filter if feature not available in whitelabel features
                if (haveWLFeature && !whiteLabelSetting?.features?.includes(WhiteLabelFeature.SignUp)) {
                    return feature;
                }

                const signupWlSetting = whiteLabelSetting?.config?.signUp;

                if (!signupWlSetting) {
                    return feature;
                }

                return {
                    ...feature,
                    signup: {
                        signupRedirect: signupWlSetting.isRedirect!,
                        signupUrl: signupWlSetting.url!,
                        emailSignup: signupWlSetting.emailSignup,
                        phoneSignup: signupWlSetting.phoneSignup,
                        emailMarketingSubscription: signupWlSetting.emailMarketingSubscription,
                    },
                };
            case FeatureName.L1Login:
                return {
                    ...feature,
                    l1Login: {},
                };
            case FeatureName.OtpLogin:
                // Filter if feature not available in whitelabel features
                if (haveWLFeature && !whiteLabelSetting?.features?.includes(WhiteLabelFeature.OtpLogin)) {
                    return feature;
                }
                return {
                    ...feature,
                    otpLogin: true,
                };
            // TODO: Override with settings from sdk
            case FeatureName.WeChatAppLogin:
                return {
                    ...feature,
                    weChatAppLogin: {},
                };
            case FeatureName.GoogleAppLogin:
                return {
                    ...feature,
                    googleAppLogin: {},
                };
            case FeatureName.FacebookAppLogin:
                return {
                    ...feature,
                    facebookAppLogin: {},
                };
            case FeatureName.LineAppLogin:
                return {
                    ...feature,
                    lineAppLogin: {},
                };
            case FeatureName.KakaoTalkLogin:
                return {
                    ...feature,
                    kakaoTalkLogin: {},
                };
            case FeatureName.UseNewPhoneValidation:
                return {
                    ...feature,
                    useNewPhoneValidation: true,
                };
            case FeatureName.SignInCallBack:
                return {
                    ...feature,
                    signInCallBack: true,
                };
            case FeatureName.UserSessionManagement:
                const userSessionManagementSetting = whiteLabelSetting?.config?.userSessionManagement;

                if (!userSessionManagementSetting) {
                    return feature;
                }
                return {
                    ...feature,
                    userSessionManagement: {
                        refreshGapInSec: userSessionManagementSetting.refreshGapInSec,
                        forceLogoutEnable: userSessionManagementSetting.forceLogoutEnable,
                        excludedPageTypeIds: userSessionManagementSetting.excludedPageTypeIds,
                        tokenExpiryInSec: userSessionManagementSetting.tokenExpiryInSec,
                        onPromptTimeInSec: userSessionManagementSetting.onPromptTimeInSec,
                        intervalTimeInSec: userSessionManagementSetting.intervalTimeInSec,
                        idleThresholdInSec: userSessionManagementSetting.idleThresholdInSec,
                        logoutUrl: userSessionManagementSetting.logoutUrl,
                    },
                };
            case FeatureName.SignInWithOtp:
                if (whiteLabelSetting?.config?.unifiedOtpLogin) {
                    return {
                        ...feature,
                        signInWithOtp: true,
                        passwordLoginOption: whiteLabelSetting?.config?.passwordLogin,
                    };
                }
            case FeatureName.WhatsAppLogin:
                // Filter if feature not available in whitelabel features
                if (whiteLabelSetting?.features?.includes(WhiteLabelFeature.WhatsAppLogin)) {
                    return {
                        ...feature,
                        whatsAppLogin: true,
                    };
                }
            case FeatureName.PasskeyAuthentication:
                if (whiteLabelSetting?.features?.includes(WhiteLabelFeature.PasskeyAuthentication)) {
                    return {
                        ...feature,
                        passkeyAuthentication: true,
                    };
                } else {
                    return {
                        ...feature,
                        passkeyAuthentication: false,
                    };
                }
            case FeatureName.WalletEnablePasskey:
                return {
                    ...feature,
                    walletEnablePasskey: true,
                };
            default:
                return feature;
        }
    }, {});
};
